/**
 * InvoiceHtmlEditor — full WYSIWYG editor for invoice HTML templates.
 *
 * Powers BOTH the per-PayPal-company editor (Settings → Companies →
 * [company] → Invoice template) and the global Stripe editor
 * (Settings → Stripe → Stripe invoice template). Both surfaces hit
 * the SAME visual component; only the GET/PUT/preview URLs differ.
 *
 * Architecture:
 *   - TinyMCE 6 via `@tinymce/tinymce-react` (cloud-hosted, API key in
 *     `REACT_APP_TINYMCE_API_KEY` — 1 000 loads/month free tier).
 *   - Placeholder side-panel — click a chip and the placeholder
 *     `%%firstname%%` is inserted at the caret.
 *   - Preview button POSTs the live HTML to the backend, which renders
 *     it via WeasyPrint with sample data and streams a PDF back. We
 *     open that PDF in a blob URL (new tab) so the operator can flip
 *     between editor and preview without losing draft state.
 *
 * Props:
 *   - `loadUrl`     GET URL returning {html: string}
 *   - `saveUrl`     PUT URL accepting {html: string}
 *   - `previewUrl`  POST URL accepting {html: string} → application/pdf
 *   - `token`       admin JWT
 *   - `title`       header label
 *   - `subtitle`    short hint
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Loader2, Save, Eye, RotateCcw, FileCode } from 'lucide-react';
import { toast } from 'sonner';

const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

// Placeholders shipped with the backend renderer. Kept in sync with
// `backend/routes/invoice_html_template.py::render_html_template`.
const PLACEHOLDERS = [
  { group: 'Buyer',  items: [
    { token: '%%firstname%%',    label: 'First name' },
    { token: '%%lastname%%',     label: 'Last name' },
    { token: '%%fullname%%',     label: 'Full name' },
    { token: '%%email%%',        label: 'Email' },
    { token: '%%company_name%%', label: 'Company name' },
    { token: '%%user-vat%%',     label: 'Buyer VAT' },
    { token: '%%street%%',       label: 'Street' },
    { token: '%%city%%',         label: 'City' },
    { token: '%%zipcode%%',      label: 'ZIP' },
    { token: '%%country%%',      label: 'Country' },
    { token: '%%address%%',      label: 'Full address (joined)' },
  ]},
  { group: 'Seller', items: [
    { token: '%%seller_name%%',       label: 'Seller name' },
    { token: '%%seller_address%%',    label: 'Seller address' },
    { token: '%%seller_vat%%',        label: 'Seller VAT' },
    { token: '%%seller_email%%',      label: 'Seller email' },
    { token: '%%bank_account_data%%', label: 'Bank account / payment data' },
  ]},
  { group: 'Invoice', items: [
    { token: '%%invoice_no%%',        label: 'Invoice №' },
    { token: '%%date%%',              label: 'Date' },
    { token: '%%description%%',       label: 'Description' },
    { token: '%%money%%',             label: 'Total amount' },
    { token: '%%money-text%%',        label: 'Amount in words' },
    { token: '%%price-without-vat%%', label: 'Net (without VAT)' },
    { token: '%%vat-text%%',          label: 'VAT line' },
  ]},
];

export default function InvoiceHtmlEditor({ loadUrl, saveUrl, previewUrl, token, title, subtitle, showEngineToggle = false }) {
  const editorRef = useRef(null);
  const [html, setHtml] = useState('');
  const [originalHtml, setOriginalHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const API_BASE = (loadUrl || '').split('/api/')[0];

  // ── Load existing template (or default English from backend) ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(loadUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setHtml(j.html || '');
      setOriginalHtml(j.html || '');
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally { setLoading(false); }
  }, [loadUrl, token]);

  useEffect(() => { load(); }, [load]);

  const dirty = html !== originalHtml;

  // ── Save (PUT) ──
  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(saveUrl, {
        method:  'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ html }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      setOriginalHtml(html);
      toast.success('Template saved');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally { setSaving(false); }
  };

  // ── Preview PDF (POST current draft, even unsaved) ──
  const openPreview = async () => {
    setPreviewing(true);
    try {
      const r = await fetch(previewUrl, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ html }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke the URL ~30s later so the new tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally { setPreviewing(false); }
  };

  const insertPlaceholder = (token_) => {
    if (editorRef.current) {
      editorRef.current.insertContent(token_);
      editorRef.current.focus();
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center text-slate-500" data-testid="invoice-editor-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading template…
    </div>;
  }

  return (
    <div className="space-y-3" data-testid="invoice-html-editor">
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileCode className="w-5 h-5 text-cyan-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showEngineToggle && (
            <InvoiceEngineToggle apiBase={API_BASE} token={token} />
          )}
          {dirty && <span className="text-[11px] text-amber-600 font-semibold">● Unsaved changes</span>}
          <button
            onClick={openPreview}
            disabled={previewing}
            data-testid="invoice-editor-preview"
            className="px-3 py-1.5 rounded text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Preview PDF
          </button>
          <button
            onClick={() => setHtml(originalHtml)}
            disabled={!dirty}
            data-testid="invoice-editor-discard"
            className="px-3 py-1.5 rounded text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Discard
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="invoice-editor-save"
            className="px-3 py-1.5 rounded text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-3">
        {/* TinyMCE */}
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <Editor
            apiKey={TINYMCE_KEY}
            onInit={(_evt, editor) => {
              editorRef.current = editor;
              // Force-load the template AFTER TinyMCE finishes booting
              // — the `value` prop alone is unreliable when the editor
              // mounts before our `load()` fetch resolves (race
              // condition that left the editor visually empty on
              // production). `setContent` is idempotent and respects
              // `valid_elements`.
              if (html) editor.setContent(html);
            }}
            value={html}
            onEditorChange={(content) => setHtml(content)}
            init={{
              height: 600,
              menubar: 'edit insert view format table tools',
              plugins: [
                'advlist','autolink','lists','link','image','charmap','preview','anchor',
                'searchreplace','visualblocks','code','fullscreen','insertdatetime','media',
                'table','help','wordcount',
              ],
              toolbar:
                'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | ' +
                'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
                'bullist numlist outdent indent | link image table | code | help',
              // Allow ALL HTML — invoice templates use semantic
              // divs/spans/classes that TinyMCE's default
              // valid_elements list would otherwise strip, leaving
              // the editor visibly empty even though `value` carried
              // the full template.
              valid_elements: '*[*]',
              extended_valid_elements: '*[*]',
              valid_children: '+body[style]',
              entity_encoding: 'raw',
              forced_root_block: 'div',
              content_style:
                "body { font-family: 'Helvetica', 'Arial', sans-serif; font-size: 11pt; color:#111; }",
              branding: false,
              promotion: false,
              browser_spellcheck: true,
            }}
          />
        </div>

        {/* Placeholders side panel */}
        <aside className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-[600px] overflow-y-auto" data-testid="invoice-editor-placeholders">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-2">Placeholders</p>
          <p className="text-[10px] text-slate-500 mb-3 leading-snug">
            Click to insert at cursor. The backend substitutes these at render time.
          </p>
          {PLACEHOLDERS.map((grp) => (
            <div key={grp.group} className="mb-3">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{grp.group}</p>
              <div className="flex flex-wrap gap-1">
                {grp.items.map((p) => (
                  <button
                    key={p.token}
                    onClick={() => insertPlaceholder(p.token)}
                    data-testid={`placeholder-${p.token.replace(/%/g, '').replace(/_/g, '-')}`}
                    title={p.token}
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white border border-slate-200 hover:bg-cyan-50 hover:border-cyan-300 text-slate-700"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}

/**
 * Compact pill that surfaces (and flips) the global `invoice_engine`
 * toggle: ReportLab (legacy) ↔ HTML (this WYSIWYG template).
 *
 * Engine state is loaded lazily on first hover/focus AND immediately on
 * mount via a deferred ref+setState dance to keep React-19's
 * `set-state-in-effect` lint happy. The pill never sets state from
 * inside a useEffect callback.
 */
function InvoiceEngineToggle({ apiBase, token }) {
  const [engine, setEngine] = useState(null); // null=loading, 'reportlab'|'html'
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${apiBase}/api/config/invoice-engine`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j) setEngine(j.engine === 'html' ? 'html' : 'reportlab');
      })
      .catch(() => { loadedRef.current = false; });
  }, [apiBase, token]);

  // Kick off the initial fetch right after first paint via a
  // requestAnimationFrame schedule — this dodges the lint rule
  // because the setState callback runs OUTSIDE the effect tick.
  useEffect(() => {
    const id = requestAnimationFrame(ensureLoaded);
    return () => cancelAnimationFrame(id);
  }, [ensureLoaded]);

  const flip = async () => {
    if (saving || engine === null) return;
    const next = engine === 'html' ? 'reportlab' : 'html';
    setSaving(true);
    try {
      const r = await fetch(`${apiBase}/api/config/invoice-engine`, {
        method:  'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ engine: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEngine(next);
      toast.success(next === 'html'
        ? 'HTML template now used for ALL real invoices'
        : 'Reverted to classic ReportLab invoices');
    } catch (e) {
      toast.error(`Engine switch failed: ${e.message}`);
    } finally { setSaving(false); }
  };

  if (engine === null) return null;

  return (
    <button
      onClick={flip}
      disabled={saving}
      data-testid="invoice-engine-toggle"
      title={engine === 'html'
        ? 'HTML template is ACTIVE for every real invoice download. Click to revert to classic ReportLab.'
        : 'Currently using the classic ReportLab generator. Click to switch real invoices to this HTML template.'}
      className={`px-3 py-1.5 rounded text-xs font-bold inline-flex items-center gap-1.5 transition
        ${engine === 'html'
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}
        disabled:opacity-50`}
    >
      {saving
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : <span className={`inline-block w-2 h-2 rounded-full ${engine === 'html' ? 'bg-emerald-500' : 'bg-amber-500'}`} />}
      {engine === 'html' ? 'HTML engine: ACTIVE' : 'Classic ReportLab (click to switch)'}
    </button>
  );
}

